import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import PropertyForm from "@/components/PropertyForm/PropertyForm";
import PropertyImagesEditor from "@/components/PropertyImagesEditor/PropertyImagesEditor";
import ApiError from "@/model/ApiError";
import PropertyFormValues from "@/model/PropertyFormValues";
import PropertyResponse from "@/model/PropertyResponse";
import propertyForm from "@/utils/propertyForm";
import "@/pages/EditPropertyPage/EditPropertyPage.scss";

function EditPropertyPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const justCreated = Boolean(
    (location.state as { justCreated?: boolean } | null)?.justCreated
  );

  const [property, setProperty] = useState<PropertyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<"notFound" | "generic" | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    propertiesApi
      .getPropertyById(session.accessToken, id)
      .then((result) => {
        if (!cancelled) {
          setProperty(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError && err.status === 404 ? "notFound" : "generic"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  if (isLoading) {
    return <p className="edit-property-page">{t("properties.edit.loading")}</p>;
  }

  if (loadError || !property) {
    return (
      <div className="edit-property-page">
        <p role="alert">
          {loadError === "notFound"
            ? t("properties.edit.notFound")
            : t("properties.edit.loadError")}
        </p>
      </div>
    );
  }

  const handleSubmit = async (values: PropertyFormValues) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Photos are managed by the editor above; keep whatever it last saved.
      await propertiesApi.updateProperty(
        session.accessToken,
        id,
        propertyForm.toRequest(values, { imageUrls: property.imageUrls ?? [] })
      );
      navigate(`/properties/${id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("properties.form.validationError"));
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t("properties.form.forbiddenError"));
      } else {
        setError(t("properties.form.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="edit-property-page">
      {justCreated && (
        <p className="edit-property-page__notice" role="status">
          {t("properties.edit.createdNotice")}
        </p>
      )}

      <PropertyImagesEditor property={property} onChange={setProperty} />

      <PropertyForm
        title={t("properties.edit.title")}
        initialValues={propertyForm.fromProperty(property)}
        submitLabel={t("properties.edit.submit")}
        isSubmitting={isSubmitting}
        error={error}
        cancelTo={`/properties/${id}`}
        showStatus
        onSubmit={handleSubmit}
      />

      <Link to={`/properties/${id}`}>{t("properties.edit.viewLink")}</Link>
    </div>
  );
}

export default EditPropertyPage;
